/* ====================================================================
   edj-gift-reveal.js — Lot 7 Batch C
   Custom element <edj-gift-reveal>.

   Lit ?recipient=NAME&message=MSG&code=CODE&from=NAME&amount=N
   Hydrate les éléments du DOM avec textContent (DOM-safe).
   Au clic "Ouvrir le présent" : enchaîne les stages 1 → 4 (timings JSX).
   Boutons 0-4 (si présents) permettent de skipper.
   Bouton "Sauvegarder le code" → copy clipboard.
   ==================================================================== */

(function () {
  'use strict';

  if (customElements.get('edj-gift-reveal')) return;

  class EdjGiftReveal extends HTMLElement {
    constructor() {
      super();
      this._stage = 0;
      this._timers = [];
      this._params = {};
    }

    connectedCallback() {
      this._readUrlParams();
      this._hydrate();
      this._setStage(0);
      this._bind();
    }

    disconnectedCallback() {
      this._clearTimers();
    }

    _readUrlParams() {
      const url = new URL(window.location.href);
      const p = url.searchParams;
      this._params = {
        recipient: p.get('recipient') || this.getAttribute('data-default-recipient') || '',
        from: p.get('from') || this.getAttribute('data-default-from') || '',
        message: p.get('message') || this.getAttribute('data-default-message') || '',
        code: p.get('code') || this.getAttribute('data-default-code') || '',
        amount: p.get('amount') || this.getAttribute('data-default-amount') || '',
      };
    }

    _hydrate() {
      // From name (3 endroits)
      this.querySelectorAll('[data-edj-from-name], [data-edj-from-name-msg], [data-edj-from-name-meta]').forEach((el) => {
        if (this._params.from) el.textContent = this._params.from;
      });
      // Mini-card "Pour <recipient>"
      this.querySelectorAll('[data-edj-mini-to]').forEach((el) => {
        el.textContent = this._params.recipient || 'vous';
      });
      // Mini-card amount
      this.querySelectorAll('[data-edj-mini-amount]').forEach((el) => {
        if (this._params.amount) el.textContent = this._params.amount;
      });
      // Stage 4 amount
      const amountFinal = this.querySelector('[data-edj-amount-final]');
      if (amountFinal && this._params.amount) amountFinal.textContent = this._params.amount;
      // Stage 4 message
      const msg = this.querySelector('[data-edj-message]');
      if (msg && this._params.message) msg.textContent = this._params.message;
      // Stage 4 code
      const code = this.querySelector('[data-edj-code]');
      if (code && this._params.code) code.textContent = this._params.code;

      // CTA "Commencer mes achats" → si code présent dans URL, on l'ajoute en query au lien
      const ctaUse = this.querySelector('[data-edj-cta-use]');
      if (ctaUse && this._params.code) {
        try {
          const baseUrl = ctaUse.getAttribute('href') || '/';
          const u = new URL(baseUrl, window.location.origin);
          u.searchParams.set('gift_code', this._params.code);
          ctaUse.setAttribute('href', u.pathname + u.search);
        } catch (err) {
          // ignore
        }
      }
    }

    _bind() {
      // Bouton "Ouvrir le présent" (stage 0 → animation séquentielle)
      const openBtn = this.querySelector('[data-edj-open]');
      if (openBtn) openBtn.addEventListener('click', () => this._startReveal());

      // Bouton replay (visible stage 4)
      const replay = this.querySelector('[data-edj-replay]');
      if (replay) replay.addEventListener('click', () => this._reset());

      // Boutons 0-4 (debug) si présents
      this.querySelectorAll('[data-edj-stage-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const n = parseInt(btn.getAttribute('data-edj-stage-btn'), 10);
          if (!Number.isNaN(n)) this._setStage(n);
        });
      });

      // Bouton sauvegarder code → clipboard
      const save = this.querySelector('[data-edj-save]');
      if (save) save.addEventListener('click', () => this._copyCode());
    }

    _setStage(n) {
      this._stage = n;
      this.setAttribute('data-stage', String(n));
      // Replay button visible stage 4
      const replay = this.querySelector('[data-edj-replay]');
      if (replay) {
        if (n === 4) replay.removeAttribute('hidden');
        else replay.setAttribute('hidden', '');
      }
      // Restart calligraphy animation au stage 4
      if (n === 4) {
        const path = this.querySelector('.edj-gift-receive__calligraphy-path');
        if (path) {
          path.style.animation = 'none';
          // force reflow
          // eslint-disable-next-line no-unused-expressions
          path.offsetHeight;
          path.style.animation = '';
        }
      }
    }

    _startReveal() {
      this._clearTimers();
      this._setStage(1);
      this._timers.push(setTimeout(() => this._setStage(2), 1100));
      this._timers.push(setTimeout(() => this._setStage(3), 2200));
      this._timers.push(setTimeout(() => this._setStage(4), 3400));
    }

    _reset() {
      this._clearTimers();
      this._setStage(0);
    }

    _clearTimers() {
      this._timers.forEach((t) => clearTimeout(t));
      this._timers = [];
    }

    async _copyCode() {
      const codeEl = this.querySelector('[data-edj-code]');
      const msg = this.querySelector('[data-edj-save-msg]');
      if (!codeEl) return;
      const text = codeEl.textContent.trim();
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // fallback : selection
          const range = document.createRange();
          range.selectNode(codeEl);
          const sel = window.getSelection();
          if (sel) {
            sel.removeAllRanges();
            sel.addRange(range);
            document.execCommand('copy');
            sel.removeAllRanges();
          }
        }
        if (msg) {
          msg.textContent = 'Code copié dans le presse-papier.';
          setTimeout(() => { if (msg.textContent === 'Code copié dans le presse-papier.') msg.textContent = ''; }, 3000);
        }
      } catch (err) {
        if (msg) msg.textContent = 'Impossible de copier — sélectionnez le code manuellement.';
      }
    }
  }

  customElements.define('edj-gift-reveal', EdjGiftReveal);
})();
